/**
 * R2 Upload Service
 * Handles media uploads for review system via edge function
 */

const FUNCTION_BASE_URL = import.meta.env.VITE_SUPABASE_URL + '/functions/v1';

export interface UploadResult {
  url: string;
  filename: string;
  size: number;
  type: string;
}

export const r2Upload = {
  /**
   * Upload review media (images/videos) to R2 via edge function
   * Edge function handles authentication and R2 upload
   */
  async uploadReviewMedia(
    file: File,
    adminId: string,
    reviewId?: string
  ): Promise<UploadResult> {
    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error(`File size ${(file.size / 1024 / 1024).toFixed(2)}MB exceeds 10MB limit`);
    }

    // Validate file type
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'video/mp4',
      'video/webm',
      'video/quicktime'
    ];

    if (!allowedTypes.includes(file.type)) {
      throw new Error(`File type ${file.type} is not supported`);
    }

    // Generate unique filename
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(7);
    const extension = file.name.split('.').pop();
    const filename = `reviews/${adminId}/${reviewId || 'temp'}/${timestamp}-${random}.${extension}`;

    // Create FormData for multipart upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', filename);
    formData.append('admin_id', adminId);
    if (reviewId) formData.append('review_id', reviewId);

    try {
      // Upload via edge function
      const response = await fetch(`${FUNCTION_BASE_URL}/upload-review-media`, {
        method: 'POST',
        body: formData,
        // Note: Don't set Content-Type header - browser will set it with boundary
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();

      return {
        url: result.url,
        filename: result.filename,
        size: file.size,
        type: file.type
      };
    } catch (error: any) {
      console.error('R2 upload error:', error);
      throw new Error(`Upload failed: ${error.message}`);
    }
  },

  /**
   * Upload multiple files with progress tracking
   */
  async uploadMultipleMedia(
    files: File[],
    adminId: string,
    reviewId?: string,
    onProgress?: (current: number, total: number) => void
  ): Promise<UploadResult[]> {
    const results: UploadResult[] = [];
    
    for (let i = 0; i < files.length; i++) {
      if (onProgress) onProgress(i + 1, files.length);
      
      try {
        const result = await this.uploadReviewMedia(files[i], adminId, reviewId);
        results.push(result);
      } catch (error: any) {
        console.error(`Failed to upload file ${files[i].name}:`, error);
        // Continue with other files even if one fails
      }
    }

    return results;
  },

  /**
   * Delete media from R2 (admin only)
   */
  async deleteReviewMedia(url: string, adminId: string): Promise<boolean> {
    try {
      const response = await fetch(`${FUNCTION_BASE_URL}/delete-review-media`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url, admin_id: adminId })
      });

      if (!response.ok) {
        throw new Error('Delete failed');
      }

      return true;
    } catch (error) {
      console.error('Delete error:', error);
      return false;
    }
  }
};
